<template>

  <div class="grid grid-cols-2 mb-[45px]">
    <div>
      <h1 class="text-5xl font-medium leading-none text-zinc-800">{{ __('Job requisitions') }}</h1>
    </div>
    <div v-if="actions?.can?.create" class="text-right">
      <a :href="route('redesign.company.requisitions.create')">
        <Button
            :content="__('Create')"
            variation="fill"
            icon="contract_edit"
        />
      </a>
    </div>
  </div>

  <div class="h-[calc(100dvh-144px)] space-y-4">

    <div class="grid gap-x-4 gap-y-2 rounded-lg bg-white p-6"
         :class="totalFilters === 2 ? 'md:grid-cols-[2.5fr_1.5fr]' : `md:grid-cols-${totalFilters}`">

      <template v-if="filters?.search?.show">
        <InputText
            id="search"
            :placeholder="__('Search')"
            :value="filters?.search?.value"
            clearable
            @update:value="handleSearchUpdate"
        />
      </template>

      <template v-if="filters?.status?.show">
        <InputMultiSelect
            class="z-40"
            :placeholder="__('Status')"
            :values="filters.status.value"
            :options="filters.status.options"
            @update:values="handleStatusUpdate"
        />
      </template>

      <template v-if="filters?.recruiters?.show">
        <InputMultiSelect
            class="z-40"
            :placeholder="__('Recruiters')"
            :values="filters.recruiters.value"
            :options="filters.recruiters.options"
            @update:values="handleRecruitorUpdate"
        />
      </template>

    </div>

    <TabGroup :selected-index="type -1" @change="handleTypeUpdate">

      <TabList class="flex gap-2 rounded-lg bg-white p-3.5">
        <Tab v-slot="{ selected }" as="template">
          <button
              :class="`${selected ? 'bg-secondary/5' : 'bg-white'} grow rounded-md px-8 py-3 font-medium capitalize leading-normal text-zinc-800 outline-none transition-colors hover:bg-secondary/5`"
          >
            {{ __('New requisition requests') }}
          </button>
        </Tab>
        <Tab v-slot="{ selected }" as="template">
          <button
              :class="`${selected ? 'bg-secondary/5' : 'bg-white'} grow rounded-md px-8 py-3 font-medium capitalize leading-normal text-zinc-800 outline-none transition-colors hover:bg-secondary/5`"
          >
            {{ __('Requisition requests taken') }}
          </button>
        </Tab>
      </TabList>

      <TabPanels class="h-[calc(100%-88px)]">
        <TabPanel class="h-[calc(100%-258px)] space-y-4 md:h-[calc(100%-206px)]">
          <NewRequisitions
            :requisitions="requisitions"
            :actions="actions"
            :is-loading="isLoading"
            @update="handleUpdateRequisitions"
          />
          <Pagination
              class="mt-6"
              :page="pagination.page"
              :pages="pagination.total"
              @update:page="handlePageChange"
          />
        </TabPanel>

        <TabPanel class="h-[calc(100%-206px)] space-y-4">

          <PostedRequisitions
            :requisitions="requisitions"
            :actions="actions"
            :is-loading="isLoading"
          />
          <Pagination
              class="mt-6"
              :page="pagination.page"
              :pages="pagination.total"
              @update:page="handlePageChange"
          />
        </TabPanel>
      </TabPanels>
    </TabGroup>
  </div>
</template>

<script setup>
  import { computed, onMounted, ref } from 'vue';
  import axios from 'axios';
  import { useToast } from 'vue-toastification';
  import { TabGroup, TabList, Tab, TabPanels, TabPanel } from '@headlessui/vue';
  import { __ } from '@/helpers';
  import { InputMultiSelect } from '@/components/form/input-multi-select';
  import { InputText } from '@/components/form/input-text';
  import { Pagination } from '@/components/block/pagination';
  import { NewRequisitions } from './components/new-requisitions';
  import { PostedRequisitions } from './components/posted-requisitions';
  import { Button } from "../../../components/global";

  const toast = useToast();

  const requisitions = ref();
  const filters = ref();
  const type = ref();
  const actions = ref();
  const totalFilters = computed(() => {
    return Object.values(filters.value ?? {})
        .filter(cfg => cfg?.show)
        .length || 1;
  })

  const pagination = ref({ page: 1, total: 1 });
  const isLoading = ref(false);

  let debounceTimeout;

  async function handleUpdateRequisitions() {
    const url = new URL(window.location.href);
    url.searchParams.delete('search');
    url.searchParams.delete('status[]');
    url.searchParams.delete('page');
    window.history.replaceState({}, '', url);

    await fetchRequisitions(url.searchParams.toString());
  }

  async function handleTypeUpdate(index) {
    const url = new URL(window.location.href);
    url.searchParams.set('type', index + 1);
    url.searchParams.delete('search');
    url.searchParams.delete('status[]');
    url.searchParams.delete('page');
    window.history.replaceState({}, '', url);

    await fetchRequisitions(url.searchParams.toString());
  }

  async function handleSearchUpdate(value) {
    clearTimeout(debounceTimeout);

    debounceTimeout = setTimeout(async () => {
      const url = new URL(window.location.href);

      if (!value) {
        url.searchParams.delete('search');
      } else {
        url.searchParams.set('search', value);
      }

      url.searchParams.delete('page');
      window.history.replaceState({}, '', url);

      await fetchRequisitions(url.searchParams.toString());
    }, 500);
  }

  async function handleStatusUpdate(values) {
    const url = new URL(window.location.href);
    url.searchParams.delete('status[]');
    values.forEach((value) => {
      url.searchParams.append('status[]', value.id);
    });
    url.searchParams.delete('page');
    window.history.replaceState({}, '', url);

    await fetchRequisitions(url.searchParams.toString());
  }

  async function handleRecruitorUpdate(values) {
    const url = new URL(window.location.href);
    console.log('thevalues', values)
    url.searchParams.delete('recruiters[]');
    values.forEach((value) => {
      url.searchParams.append('recruiters[]', value.id);
    });
    url.searchParams.delete('page');
    window.history.replaceState({}, '', url);

    await fetchRequisitions(url.searchParams.toString());
  }

  async function handlePageChange(index) {
    const url = new URL(window.location.href);
    url.searchParams.set('page', index);
    window.history.replaceState({}, '', url);

    await fetchRequisitions(url.searchParams.toString());
  }

  async function fetchRequisitions(params) {
    const baseUri = route('redesign.company.requisitions.load');
    const uri = params ? `${baseUri}?${params}` : baseUri;

    isLoading.value = true;

    try {
      const response = await axios.get(uri);

      requisitions.value = response.data.requisitions;
      filters.value = response.data.filters;
      type.value = response.data.type;
      actions.value = response.data.actions;
      pagination.value = response.data.pagination;
      console.log('returned', response.data);
      console.log('the actions', actions.value);
    } catch (error) {
      if (error?.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(__('An unexpected error has occured. Please try again later.'));
      }
    } finally {
      isLoading.value = false;
    }

  }

  onMounted(async () => {
    const searchParams = new URL(window.location.href).searchParams.toString();

    await fetchRequisitions(searchParams);
  });
</script>
